/* eslint-disable radix */
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useAuth } from '@clerk/clerk-react';
import LoadingScreen from '../../components/LoadingScreen';
import AccessDenied from '../../views/auth/AccessDenied';
import { ME_API_URL } from '../../utils/apiUrl';

const TEMPLATE =
  process.env.REACT_APP_CLEARK_TEMPLATE_ID ||
  process.env.REACT_APP_CLERK_TEMPLATE_ID;

async function fetchIsCreator(getToken, { skipCache = false } = {}) {
  const token = TEMPLATE
    ? await getToken({ template: TEMPLATE, skipCache })
    : await getToken({ skipCache });
  if (!token) {
    return false;
  }
  const { data } = await axios.get(
    `${process.env.REACT_APP_API_URL}${ME_API_URL}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return data?.is_creator === true;
}

const UserGuard = ({ children }) => {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setAllowed(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const isCreator = await fetchIsCreator(getToken);
        if (!cancelled) setAllowed(isCreator);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          try {
            const isCreator = await fetchIsCreator(getToken, { skipCache: true });
            if (!cancelled) setAllowed(isCreator);
            return;
          } catch {
            // still unauthorized
          }
        }
        if (!cancelled) setAllowed(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken]);

  if (!isLoaded) {
    return <LoadingScreen />;
  }
  if (!isSignedIn) {
    return <Navigate to="/login" replace />;
  }
  if (allowed === null) {
    return <LoadingScreen />;
  }
  if (!allowed) {
    return <AccessDenied />;
  }
  return <>{children}</>;
};

UserGuard.propTypes = {
  children: PropTypes.node.isRequired,
};

export default UserGuard;
